import React, { useState } from 'react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, Line, ComposedChart } from 'recharts';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const MonthComparisonChart = ({ data }) => {
  const [chartType, setChartType] = useState('bar');

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload?.length) {
      const budget = payload?.find(p => p?.dataKey === 'budget')?.value;
      const actual = payload?.find(p => p?.dataKey === 'actual')?.value;
      const difference = actual - budget;
      const isOver = difference > 0;

      return (
        <div className="bg-popover border border-border rounded-lg p-3 shadow-elevation-3">
          <p className="text-sm font-medium text-foreground mb-2">{payload?.[0]?.payload?.month}</p>
          <div className="space-y-1">
            <div className="flex items-center justify-between gap-4">
              <span className="text-xs text-muted-foreground">Budget:</span>
              <span className="text-xs font-semibold text-primary">${budget?.toLocaleString()}</span>
            </div>
            <div className="flex items-center justify-between gap-4">
              <span className="text-xs text-muted-foreground">Actual:</span>
              <span className="text-xs font-semibold text-secondary">${actual?.toLocaleString()}</span>
            </div>
            <div className="flex items-center justify-between gap-4 pt-1 border-t border-border">
              <span className="text-xs text-muted-foreground">Difference:</span>
              <span className={`text-xs font-semibold ${isOver ? 'text-error' : 'text-success'}`}>
                {isOver ? '+' : ''}${difference?.toLocaleString()}
              </span>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  const calculateStats = () => {
    const totalBudget = data?.reduce((sum, item) => sum + item?.budget, 0);
    const totalActual = data?.reduce((sum, item) => sum + item?.actual, 0);
    const avgBudget = totalBudget / data?.length;
    const avgActual = totalActual / data?.length;
    const variance = totalActual - totalBudget;
    const variancePercentage = ((variance / totalBudget) * 100)?.toFixed(1);

    return { totalBudget, totalActual, avgBudget, avgActual, variance, variancePercentage };
  };

  const stats = calculateStats();

  return (
    <div className="bg-card rounded-2xl p-4 md:p-6 shadow-elevation-2">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-info/10 flex items-center justify-center">
            <Icon name="BarChart3" size={20} color="var(--color-info)" />
          </div>
          <div>
            <h2 className="text-lg md:text-xl font-semibold text-foreground">Month-to-Month Comparison</h2>
            <p className="text-sm text-muted-foreground">Budget vs actual spending trends</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant={chartType === 'bar' ? 'default' : 'outline'}
            size="sm"
            iconName="BarChart3"
            onClick={() => setChartType('bar')}
          >
            Bar
          </Button>
          <Button
            variant={chartType === 'line' ? 'default' : 'outline'}
            size="sm"
            iconName="LineChart"
            onClick={() => setChartType('line')}
          >
            Line
          </Button>
        </div>
      </div>

      <div className="w-full h-64 md:h-80 mb-6" aria-label="Budget vs Actual Spending Comparison Chart">
        <ResponsiveContainer width="100%" height="100%">
          {chartType === 'bar' ? (
            <BarChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="budgetGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="var(--color-primary)" stopOpacity={0.8} />
                  <stop offset="95%" stopColor="var(--color-primary)" stopOpacity={0.3} />
                </linearGradient>
                <linearGradient id="actualGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="var(--color-secondary)" stopOpacity={0.8} />
                  <stop offset="95%" stopColor="var(--color-secondary)" stopOpacity={0.3} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis 
                dataKey="month" 
                stroke="var(--color-muted-foreground)" 
                style={{ fontSize: '12px' }}
              />
              <YAxis 
                stroke="var(--color-muted-foreground)" 
                style={{ fontSize: '12px' }}
                tickFormatter={(value) => `$${(value / 1000)?.toFixed(0)}k`}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend 
                wrapperStyle={{ fontSize: '12px' }}
                iconType="circle"
              />
              <Bar 
                dataKey="budget" 
                fill="url(#budgetGradient)" 
                radius={[8, 8, 0, 0]}
                name="Budget"
              />
              <Bar 
                dataKey="actual" 
                fill="url(#actualGradient)" 
                radius={[8, 8, 0, 0]}
                name="Actual"
              />
            </BarChart>
          ) : (
            <ComposedChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="budgetAreaGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="var(--color-primary)" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="var(--color-primary)" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="actualAreaGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="var(--color-secondary)" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="var(--color-secondary)" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis 
                dataKey="month" 
                stroke="var(--color-muted-foreground)" 
                style={{ fontSize: '12px' }}
              />
              <YAxis 
                stroke="var(--color-muted-foreground)" 
                style={{ fontSize: '12px' }}
                tickFormatter={(value) => `$${(value / 1000)?.toFixed(0)}k`}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend 
                wrapperStyle={{ fontSize: '12px' }}
                iconType="circle"
              />
              <Line 
                type="monotone" 
                dataKey="budget" 
                stroke="var(--color-primary)" 
                strokeWidth={3}
                dot={{ fill: 'var(--color-primary)', r: 4 }}
                name="Budget"
              />
              <Line 
                type="monotone" 
                dataKey="actual" 
                stroke="var(--color-secondary)" 
                strokeWidth={3}
                dot={{ fill: 'var(--color-secondary)', r: 4 }}
                name="Actual"
              />
            </ComposedChart>
          )}
        </ResponsiveContainer>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-border">
        <div className="text-center">
          <p className="text-xs text-muted-foreground mb-1">Avg. Budget</p>
          <p className="text-base md:text-lg font-semibold text-primary">
            ${stats?.avgBudget?.toLocaleString(undefined, { maximumFractionDigits: 0 })}
          </p>
        </div>
        <div className="text-center">
          <p className="text-xs text-muted-foreground mb-1">Avg. Actual</p>
          <p className="text-base md:text-lg font-semibold text-secondary">
            ${stats?.avgActual?.toLocaleString(undefined, { maximumFractionDigits: 0 })}
          </p>
        </div>
        <div className="text-center">
          <p className="text-xs text-muted-foreground mb-1">Total Variance</p>
          <p className={`text-base md:text-lg font-semibold ${
            stats?.variance > 0 ? 'text-error' : 'text-success'
          }`}>
            {stats?.variance > 0 ? '+' : ''}${stats?.variance?.toLocaleString(undefined, { maximumFractionDigits: 0 })}
          </p>
        </div>
        <div className="text-center">
          <p className="text-xs text-muted-foreground mb-1">Variance %</p>
          <p className={`text-base md:text-lg font-semibold ${
            stats?.variance > 0 ? 'text-error' : 'text-success'
          }`}>
            {stats?.variancePercentage}%
          </p>
        </div>
      </div>
    </div>
  );
};

export default MonthComparisonChart;